import type { UseDraggableArguments } from "@dnd-kit/core";
import { DndContext, type DndContextProps, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { useSortable } from "@dnd-kit/sortable";
import { clsx } from "clsx";
import { Children, type CSSProperties, type FC, type PropsWithChildren } from "react";
import { SymbolIcon, TriggerButton } from "widgets";

import style from "./styles/Sortable.module.scss";

// TODO: Have a look on react-aria useList* and replace with it if possible (check useMove for animation)

const SortableDragHandleBasicWrapper = () => <SymbolIcon icon="drag_indicator" className={style.drag_handle} />;

// --- Container ---
interface SortableList extends DndContextProps {
  id?: string;
}

const SortableList: FC<PropsWithChildren<DndContextProps>> = ({ children, id, ...sortableProps }) => {
  const pointerSensor = useSensor(MouseSensor, { activationConstraint: { distance: 5 } });
  const sensors = useSensors(pointerSensor);
  return (
    <DndContext sensors={sensors} {...sortableProps} modifiers={[restrictToParentElement]}>
      <div id={id} className={style.list}>
        {children}
      </div>
    </DndContext>
  );
};

// --- Item ---
interface SortableItemProps extends UseDraggableArguments {
  // provided: id, data, disabled
  index: number;
  label?: string; // For accessibility only
  removeDisabled?: boolean;
  onRemove?: (index: number) => void;
  removeTooltip?: string;
  customStyle?: CSSProperties;
}

const SortableItem: FC<PropsWithChildren<SortableItemProps>> = ({
  children,
  id,
  label,
  onRemove,
  removeTooltip,
  removeDisabled,
  customStyle,
  disabled,
  index,
}) => {
  const childrenAsArray = Children.toArray(children);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: id,
    disabled: disabled,
  });
  const dragStyle = transform ? { transform: `translate3d( 0,${transform.y}px, 0)`, transition } : undefined;

  return (
    <div
      id="formControlsText"
      className={style.sortable}
      style={{ ...customStyle, ...dragStyle }}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <div className={style.sortable_container}>
        {!disabled && <SortableDragHandleBasicWrapper />}
        {childrenAsArray?.[0]}
      </div>
      <div className={clsx(style.sortable_container, style.reduced_column_gap)}>
        {childrenAsArray?.[1]}
        {!removeDisabled && (
          <TriggerButton
            onPress={() => {
              onRemove?.(index);
            }}
            isDisabled={typeof onRemove !== "function"}
            label="Remove"
            // Empty string are falsy, do not use nullish coalescing operator (??) here

            tooltip={removeTooltip || `Remove ${label || ""}`}
            icon="close"
          />
        )}
      </div>
    </div>
  );
};

export { SortableDragHandleBasicWrapper, SortableItem, SortableList };
